import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Play, X, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import videoThumbnail from '@/assets/intro-video-thumbnail.jpg';
import { CinematicVideoModal } from './CinematicVideoModal';

const copy = {
  en: {
    headline: 'Some words should be heard while people are still alive.',
    sub: 'Create heartfelt Living Announcements for the people who matter most.',
    primary: 'Create Your Message',
    secondary: 'Watch Our Story',
    tap: 'Tap to play · 1 min',
    whoHeading: 'Who do you need to Say It Now to?',
    whoSub: 'Choose someone. We\'ll help you say what matters.',
    continue: 'Continue',
    cats: {
      mom: 'Mom', dad: 'Dad', spouse: 'Spouse', friend: 'Friend',
      child: 'Child', miss: 'Someone I miss', forgive: 'Someone I need to forgive'
    }
  },
  es: {
    headline: 'Algunas palabras deben oírse mientras las personas aún están vivas.',
    sub: 'Crea Anuncios Vivientes sinceros para quienes más importan.',
    primary: 'Crea Tu Mensaje', secondary: 'Mira Nuestra Historia',
    tap: 'Toca para reproducir · 1 min',
    whoHeading: '¿A quién necesitas decírselo ahora?',
    whoSub: 'Elige a alguien. Te ayudamos a decir lo que importa.',
    continue: 'Continuar',
    cats: { mom: 'Mamá', dad: 'Papá', spouse: 'Pareja', friend: 'Amigo', child: 'Hijo/a', miss: 'Alguien que extraño', forgive: 'Alguien a quien debo perdonar' }
  },
  pt: {
    headline: 'Algumas palavras precisam ser ouvidas enquanto as pessoas ainda estão vivas.',
    sub: 'Crie Anúncios Vivos sinceros para quem mais importa.',
    primary: 'Crie Sua Mensagem', secondary: 'Assista Nossa História',
    tap: 'Toque para reproduzir · 1 min',
    whoHeading: 'Para quem você precisa dizer agora?',
    whoSub: 'Escolha alguém. Te ajudamos a dizer o que importa.',
    continue: 'Continuar',
    cats: { mom: 'Mãe', dad: 'Pai', spouse: 'Cônjuge', friend: 'Amigo', child: 'Filho(a)', miss: 'Alguém de quem sinto falta', forgive: 'Alguém que preciso perdoar' }
  },
  fr: {
    headline: 'Certains mots devraient être entendus tant que les gens sont encore là.',
    sub: 'Créez des Annonces Vivantes sincères pour ceux qui comptent le plus.',
    primary: 'Créer Votre Message', secondary: 'Regardez Notre Histoire',
    tap: 'Appuyez pour lire · 1 min',
    whoHeading: 'À qui devez-vous le dire maintenant ?',
    whoSub: 'Choisissez quelqu\'un. Nous vous aidons à dire ce qui compte.',
    continue: 'Continuer',
    cats: { mom: 'Maman', dad: 'Papa', spouse: 'Conjoint·e', friend: 'Ami·e', child: 'Enfant', miss: 'Quelqu\'un qui me manque', forgive: 'Quelqu\'un à pardonner' }
  },
  ar: {
    headline: 'بعض الكلمات يجب أن تُسمع بينما لا يزال الناس على قيد الحياة.',
    sub: 'أنشئ إعلانات حية صادقة للأشخاص الأكثر أهمية.',
    primary: 'أنشئ رسالتك', secondary: 'شاهد قصتنا',
    tap: 'اضغط للتشغيل · دقيقة واحدة',
    whoHeading: 'لمن تحتاج أن تقولها الآن؟',
    whoSub: 'اختر شخصاً. سنساعدك على قول ما يهم.',
    continue: 'متابعة',
    cats: { mom: 'أمي', dad: 'أبي', spouse: 'الشريك', friend: 'صديق', child: 'طفلي', miss: 'شخص أفتقده', forgive: 'شخص أحتاج أن أسامحه' }
  },
  ja: {
    headline: 'ある言葉は、その人が生きているうちに届けるべきです。',
    sub: '大切な人へ、心からのリビング・アナウンスメントを。',
    primary: 'メッセージを作る', secondary: '私たちのストーリーを見る',
    tap: 'タップで再生 · 1分',
    whoHeading: '今、誰に伝えたいですか？',
    whoSub: '相手を選んでください。大切な言葉を一緒に紡ぎます。',
    continue: '続ける',
    cats: { mom: 'お母さん', dad: 'お父さん', spouse: '配偶者', friend: '友達', child: '子ども', miss: '会いたい人', forgive: '許したい人' }
  },
  ko: {
    headline: '어떤 말은 사람이 살아 있을 때 전해져야 합니다.',
    sub: '가장 소중한 사람에게 진심 어린 살아있는 메시지를 보내세요.',
    primary: '메시지 만들기', secondary: '우리의 이야기 보기',
    tap: '탭하여 재생 · 1분',
    whoHeading: '지금 누구에게 전하고 싶나요?',
    whoSub: '한 사람을 선택하세요. 함께 마음을 전합니다.',
    continue: '계속',
    cats: { mom: '엄마', dad: '아빠', spouse: '배우자', friend: '친구', child: '자녀', miss: '그리운 사람', forgive: '용서할 사람' }
  },
  id: {
    headline: 'Beberapa kata harus didengar saat orang masih hidup.',
    sub: 'Buat Pengumuman Hidup yang tulus untuk orang-orang terpenting.',
    primary: 'Buat Pesan Anda', secondary: 'Tonton Cerita Kami',
    tap: 'Ketuk untuk memutar · 1 menit',
    whoHeading: 'Kepada siapa Anda perlu mengatakannya sekarang?',
    whoSub: 'Pilih seseorang. Kami bantu menyampaikan yang penting.',
    continue: 'Lanjutkan',
    cats: { mom: 'Ibu', dad: 'Ayah', spouse: 'Pasangan', friend: 'Teman', child: 'Anak', miss: 'Seseorang yang dirindukan', forgive: 'Seseorang untuk dimaafkan' }
  },
  hi: {
    headline: 'कुछ बातें तब कही जानी चाहिए जब लोग अभी भी जीवित हों।',
    sub: 'सबसे महत्वपूर्ण लोगों के लिए हार्दिक लिविंग अनाउंसमेंट बनाएँ।',
    primary: 'अपना संदेश बनाएँ', secondary: 'हमारी कहानी देखें',
    tap: 'चलाने के लिए टैप करें · 1 मिनट',
    whoHeading: 'आपको अभी किसे कहना है?',
    whoSub: 'किसी को चुनें। हम आपकी बात कहने में मदद करेंगे।',
    continue: 'जारी रखें',
    cats: { mom: 'माँ', dad: 'पिताजी', spouse: 'जीवनसाथी', friend: 'दोस्त', child: 'बच्चा', miss: 'कोई जिसे मैं याद करता हूँ', forgive: 'कोई जिसे माफ करना है' }
  },
};

const CATEGORIES = [
  { key: 'mom', emoji: '🌷', tone: 'from-rose-500/20 to-pink-500/10' },
  { key: 'dad', emoji: '🌿', tone: 'from-emerald-500/20 to-teal-500/10' },
  { key: 'spouse', emoji: '💞', tone: 'from-fuchsia-500/20 to-rose-500/10' },
  { key: 'friend', emoji: '✨', tone: 'from-amber-400/20 to-orange-400/10' },
  { key: 'child', emoji: '🌟', tone: 'from-sky-400/20 to-indigo-400/10' },
  { key: 'miss', emoji: '🕊️', tone: 'from-violet-400/20 to-blue-400/10' },
  { key: 'forgive', emoji: '🤍', tone: 'from-stone-400/20 to-zinc-400/10' },
] as const;

export function CinematicHero() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = lc(copy, language);
  const [open, setOpen] = useState(false);
  const [showWho, setShowWho] = useState(false);
  const whoRef = useRef<HTMLDivElement>(null);

  // Body scroll lock is handled by CinematicVideoModal

  const handleClose = () => {
    setOpen(false);
    setShowWho(true);
    setTimeout(() => {
      whoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 350);
  };

  const handlePickCategory = (key: string) => {
    try { sessionStorage.setItem('say_it_now_recipient_hint', key); } catch {}
    navigate(`/try?to=${encodeURIComponent(key)}`);
  };

  return (
    <>
      <section className="relative isolate overflow-hidden min-h-[100dvh] md:min-h-[92vh] flex items-center pt-20 pb-12 md:py-24">
        {/* Cinematic ambient layers */}
        <div aria-hidden className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background to-[hsl(var(--primary)/0.05)]" />
          <motion.div
            className="absolute top-[20%] left-1/2 -translate-x-1/2 h-[600px] w-[600px] md:h-[900px] md:w-[900px] rounded-full"
            style={{ background: 'radial-gradient(circle, hsl(var(--primary)/0.18), transparent 60%)' }}
            animate={{ scale: [1, 1.08, 1], opacity: [0.7, 1, 0.7] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            className="absolute bottom-[10%] right-[10%] h-[300px] w-[300px] md:h-[500px] md:w-[500px] rounded-full blur-3xl"
            style={{ background: 'hsl(var(--secondary)/0.18)' }}
            animate={{ scale: [1, 1.15, 1], opacity: [0.4, 0.8, 0.4] }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
          />
          {/* Drifting motes */}
          {[...Array(8)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute h-1.5 w-1.5 rounded-full bg-primary/40"
              style={{ left: `${(i * 13 + 7) % 100}%`, top: `${(i * 21 + 12) % 100}%` }}
              animate={{ y: [0, -40, 0], opacity: [0, 0.8, 0] }}
              transition={{ duration: 7 + i, repeat: Infinity, ease: 'easeInOut', delay: i * 0.6 }}
            />
          ))}
        </div>

        <div className="container relative z-10 max-w-5xl mx-auto px-5 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
            className="font-heading font-extrabold tracking-tight text-foreground leading-[1.1] text-[2rem] sm:text-5xl md:text-6xl lg:text-7xl billboard-flash max-w-4xl mx-auto"
          >
            {t.headline}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.4 }}
            className="mt-5 md:mt-7 text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed"
          >
            {t.sub}
          </motion.p>

          {/* Cinematic video thumbnail */}
          <motion.button
            initial={{ opacity: 0, scale: 0.96, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.55, ease: [0.16, 1, 0.3, 1] }}
            onClick={() => setOpen(true)}
            aria-label={t.secondary}
            className="group relative block mx-auto mt-8 md:mt-12 w-full max-w-3xl aspect-video rounded-2xl md:rounded-3xl overflow-hidden shadow-[0_30px_80px_-20px_hsl(var(--primary)/0.4)] ring-1 ring-white/10 active:scale-[0.99] transition-transform"
          >
            <img src={videoThumbnail} alt="Say It NOW story preview" className="absolute inset-0 w-full h-full object-cover scale-105 group-hover:scale-110 transition-transform duration-[1500ms]" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/30" />

            {/* Pulsing glow rings */}
            <span aria-hidden className="absolute inset-0 flex items-center justify-center">
              <motion.span
                className="absolute h-24 w-24 md:h-32 md:w-32 rounded-full bg-primary/30"
                animate={{ scale: [1, 1.6, 1], opacity: [0.6, 0, 0.6] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: 'easeOut' }}
              />
              <motion.span
                className="absolute h-24 w-24 md:h-32 md:w-32 rounded-full bg-primary/20"
                animate={{ scale: [1, 2, 1], opacity: [0.4, 0, 0.4] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: 'easeOut', delay: 0.6 }}
              />
            </span>

            <span className="relative z-10 flex flex-col items-center justify-center h-full">
              <span className="h-20 w-20 md:h-24 md:w-24 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-2xl shadow-primary/40 group-hover:scale-110 transition-transform">
                <Play className="h-9 w-9 md:h-11 md:w-11 ml-1" fill="currentColor" />
              </span>
              <span className="mt-4 text-white/90 text-xs md:text-sm font-medium tracking-wide drop-shadow">{t.tap}</span>
            </span>
          </motion.button>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-8 md:mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4"
          >
            <Button
              size="lg"
              onClick={() => navigate('/try')}
              className="gap-2 text-base px-8 py-6 rounded-2xl shadow-lg shadow-primary/30 w-full sm:w-auto"
            >
              <Heart className="h-5 w-5" fill="currentColor" />
              {t.primary}
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => setOpen(true)}
              className="gap-2 text-base px-8 py-6 rounded-2xl border-border/60 w-full sm:w-auto"
            >
              <Play className="h-4 w-4" fill="currentColor" />
              {t.secondary}
            </Button>
          </motion.div>
        </div>
      </section>

      {/* "Who do you need to Say It Now to?" — appears after video */}
      <AnimatePresence>
        {showWho && (
          <motion.section
            ref={whoRef}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="relative py-16 md:py-24 overflow-hidden"
          >
            <div aria-hidden className="absolute inset-0 -z-10 bg-gradient-to-b from-[hsl(var(--primary)/0.08)] via-background to-background" />
            <div className="container max-w-5xl mx-auto px-5 text-center">
              <h2 className="font-heading font-extrabold text-3xl md:text-5xl text-foreground mb-3 billboard-flash">
                {t.whoHeading}
              </h2>
              <p className="text-muted-foreground md:text-lg mb-10 md:mb-14">{t.whoSub}</p>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
                {CATEGORIES.map((cat, i) => (
                  <motion.button
                    key={cat.key}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                    whileHover={{ y: -4, scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handlePickCategory(cat.key)}
                    className={`group relative aspect-[4/5] sm:aspect-square rounded-2xl border border-border/40 bg-gradient-to-br ${cat.tone} backdrop-blur-sm p-4 md:p-6 flex flex-col items-center justify-center text-center overflow-hidden transition-shadow hover:shadow-xl hover:shadow-primary/10`}
                  >
                    <motion.span
                      className="text-4xl md:text-5xl mb-2 md:mb-3"
                      animate={{ y: [0, -4, 0] }}
                      transition={{ duration: 4 + i * 0.3, repeat: Infinity, ease: 'easeInOut' }}
                    >
                      {cat.emoji}
                    </motion.span>
                    <span className="font-heading font-semibold text-sm md:text-base text-foreground leading-tight">
                      {(t.cats as Record<string, string>)[cat.key]}
                    </span>
                    <ArrowRight className="absolute bottom-3 right-3 h-4 w-4 text-foreground/40 group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
                  </motion.button>
                ))}
              </div>
            </div>
          </motion.section>
        )}
      </AnimatePresence>

      {/* Cinematic fullscreen video modal */}
      <CinematicVideoModal open={open} onClose={handleClose} />
    </>
  );
}
